import {
  Accordion,
  Button,
  Flex,
  Icon,
  List,
  Typography,
} from "@smartfaster-ui/components";
import { useTranslation } from "react-i18next";

import { useCompactLayout } from "../../../hooks";
import { BUTTON_ICON_NAMES, BUTTON_LABEL_KEYS } from "../context/constants";
import { useInteriorDesigner } from "../context/InteriorDesignerContext";
import { getValidSuggestions, isCtaEnabled } from "../context/selectors";
import type { GenerationAction, PhotoSuggestion } from "../context/types";
import { TARGET_PANEL_TO_PANEL_ID } from "../context/types";
import { ExtraWishesInput } from "../selections/ExtraWishesInput";
import { Panel } from "./Panel";

export function WishesAndSuggestionsPanel() {
  const { t } = useTranslation();
  const { state, dispatch } = useInteriorDesigner();
  const { isCompact } = useCompactLayout();
  const selections = state.panelSelections.otherWishes;
  const ctaEnabled = isCtaEnabled({ state, panelId: "otherWishes" });

  const validSuggestions = getValidSuggestions({ state });

  const handleApply = ({ suggestion }: { suggestion: PhotoSuggestion }) => {
    const panelId = TARGET_PANEL_TO_PANEL_ID[suggestion.target_panel];
    dispatch({
      type: "SUGGESTION_APPLIED",
      panel: panelId,
      prefill: suggestion.structured_prefill,
      freeText: suggestion.free_text_prefill,
    });
  };

  const handleCtaClick = () => {
    const actions: GenerationAction[] = [
      {
        type: "other_wishes",
        text: selections.text,
      },
    ];
    dispatch({ type: "GENERATION_REQUESTED", actions });
  };

  return (
    <Panel
      ctaLabel={t("interiorDesigner.otherWishes.cta")}
      ctaHintText={t("interiorDesigner.otherWishes.ctaHint")}
      ctaEnabled={ctaEnabled}
      onCtaClick={handleCtaClick}
      requiresVocab={false}
    >
      <Accordion
        multiple
        keepMounted
        defaultValue={["suggestions", "wishes"]}
        variant="filled"
        color="surfaceSecondary"
      >
        <Accordion.Item
          value="suggestions"
          title={t("interiorDesigner.wishesAndSuggestions.suggestionsHeading")}
        >
          {validSuggestions.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              padding="lg"
            >
              <Typography variant="bodySm" color="foregroundSecondary">
                {t("interiorDesigner.suggestions.empty")}
              </Typography>
            </Flex>
          ) : (
            <Flex
              direction="column"
              paddingY="sm"
              background="surfaceNeutralSecondary"
              radius="lg"
            >
              <List variant="separated" color="surfaceNeutralPrimary">
                {validSuggestions.map((suggestion) => {
                  const panelId =
                    TARGET_PANEL_TO_PANEL_ID[suggestion.target_panel];
                  const displayLabel =
                    suggestion.label || t(BUTTON_LABEL_KEYS[panelId]);

                  const applyButton = (
                    <Button
                      variant="filled"
                      color="primary"
                      size="sm"
                      onClick={() => handleApply({ suggestion })}
                    >
                      {t("interiorDesigner.suggestions.apply")}
                    </Button>
                  );

                  return (
                    <List.Row
                      key={suggestion.id}
                      leading={
                        <Icon
                          name={BUTTON_ICON_NAMES[panelId]}
                          color="foregroundSecondary"
                        />
                      }
                      trailing={isCompact ? undefined : applyButton}
                    >
                      <Flex
                        direction="column"
                        align="start"
                        gap="xs"
                        paddingY="xs"
                      >
                        <Typography variant="bodySm" weight="semibold">
                          {displayLabel}
                        </Typography>
                        {suggestion.rationale && (
                          <Typography
                            variant="bodyXs"
                            color="foregroundSecondary"
                          >
                            {suggestion.rationale}
                          </Typography>
                        )}
                        {isCompact && applyButton}
                      </Flex>
                    </List.Row>
                  );
                })}
              </List>
            </Flex>
          )}
        </Accordion.Item>

        <Accordion.Item
          value="wishes"
          title={t("interiorDesigner.wishesAndSuggestions.wishesHeading")}
        >
          <ExtraWishesInput
            value={selections.text}
            onValueChange={({ value }) =>
              dispatch({
                type: "SELECTION_CHANGED",
                panel: "otherWishes",
                field: "text",
                value,
              })
            }
          />
        </Accordion.Item>
      </Accordion>
    </Panel>
  );
}
